(() => {
  const REACT_MOUNT_TIMEOUT_MS = 3000;
  const MAX_SERIALIZATION_DEPTH = 3;

  if (window.__bezonedGlobalErrorHandlersInstalled) {
    return;
  }

  window.__bezonedGlobalErrorHandlersInstalled = true;

  // Simple fallback for when React fails to load entirely
  function showCatastrophicError() {
    try {
      const target = document.querySelector("#root") || document.body;
      if (target) {
        target.innerHTML = `
          <div style="min-height:100vh;display:flex;align-items:center;justify-content:center;font-family:system-ui,sans-serif;padding:24px;">
            <div style="text-align:center;max-width:400px;">
              <p style="color:#4b5563;margin-bottom:16px;">Something went wrong. Please refresh the page.</p>
              <button onclick="location.reload()" style="padding:8px 16px;background:#111827;color:#fff;border:none;border-radius:6px;cursor:pointer;">
                Refresh
              </button>
            </div>
          </div>
        `;
      }
    } catch (error) {
      console.error("Failed to show catastrophic error message", error);
    }
  }

  function getEventTargetClassName(target) {
    if (typeof target.className === "string") {
      return target.className || undefined;
    }

    const classAttribute = target.getAttribute("class");
    return classAttribute || undefined;
  }

  function getOptionalTargetProperty(target, property) {
    return property in target ? target[property] || undefined : undefined;
  }

  function getEventTargetDetails(target) {
    if (!(target instanceof HTMLElement || target instanceof SVGElement)) {
      return null;
    }

    return {
      className: getEventTargetClassName(target),
      currentSrc: getOptionalTargetProperty(target, "currentSrc"),
      href: getOptionalTargetProperty(target, "href"),
      id: target.id || undefined,
      src: getOptionalTargetProperty(target, "src"),
      tagName: target.tagName,
    };
  }

  function getErrorDetails(error, seen) {
    const details = {
      message: error.message,
      name: error.name,
      stack: error.stack,
    };

    if ("cause" in error) {
      details.cause = serializeForSentry(error.cause, seen);
    }

    for (const key of Object.keys(error)) {
      details[key] = serializeForSentry(error[key], seen);
    }

    return details;
  }

  function isSerializablePrimitive(value) {
    const type = typeof value;
    return type === "string" || type === "number" || type === "boolean";
  }

  function isSpecialSerializable(value) {
    const type = typeof value;
    return type === "bigint" || type === "symbol" || type === "function";
  }

  function serializeArrayForSentry(value, seen, depth) {
    return value.map((item) => serializeForSentry(item, seen, depth + 1));
  }

  function serializePlainObjectForSentry(value, seen, depth) {
    const result = {};
    for (const key of Object.keys(value)) {
      result[key] = serializeForSentry(value[key], seen, depth + 1);
    }

    return result;
  }

  function serializeObjectForSentry(value, seen, depth) {
    if (Array.isArray(value)) {
      return serializeArrayForSentry(value, seen, depth);
    }

    return serializePlainObjectForSentry(value, seen, depth);
  }

  function serializeErrorForSentry(value, seen) {
    if (seen.has(value)) {
      return "[Circular Error]";
    }

    seen.add(value);
    return getErrorDetails(value, seen);
  }

  function serializeComplexValueForSentry(value, seen, depth) {
    if (seen.has(value)) {
      return "[Circular]";
    }

    if (depth >= MAX_SERIALIZATION_DEPTH) {
      return Object.prototype.toString.call(value);
    }

    seen.add(value);
    return serializeObjectForSentry(value, seen, depth);
  }

  function serializeForSentry(value, seen = new WeakSet(), depth = 0) {
    if (value === null || value === undefined) {
      return value;
    }

    if (value instanceof Error) {
      return serializeErrorForSentry(value, seen);
    }

    if (isSerializablePrimitive(value)) {
      return value;
    }

    if (isSpecialSerializable(value)) {
      return String(value);
    }

    return serializeComplexValueForSentry(value, seen, depth);
  }

  function stringifyForSentry(value) {
    if (value instanceof Error) {
      return value.stack || `${value.name}: ${value.message}`;
    }

    if (typeof value === "string") {
      return value;
    }

    try {
      return JSON.stringify(serializeForSentry(value));
    } catch {
      return String(value);
    }
  }

  function normalizeToError(value) {
    if (value instanceof Error) {
      return value;
    }

    return new Error(stringifyForSentry(value));
  }

  function logFatalError(label, error, details) {
    try {
      console.error(`[global-error-handler] ${label}`, error, details);
    } catch {
      // Ignore secondary logging failures.
    }
  }

  // Only show DOM fallback if React never mounts within a timeout
  let reactMountTimeout = null;

  function handleFatalError(error, extra) {
    // Always report to Sentry if available
    if (window.Sentry?.captureException) {
      window.Sentry.captureException(normalizeToError(error), {
        extra: serializeForSentry(extra),
        tags: {
          source: "global-error-handler",
        },
      });
    }

    // If React is already mounted, don't do anything visual -
    // ErrorBoundary will handle render errors, and async errors
    // shouldn't break the UI
    if (window.__bezonedReactMounted) {
      return;
    }

    // Give React a chance to mount and handle the error via ErrorBoundary
    // Only show DOM fallback if React never mounts
    if (!reactMountTimeout) {
      reactMountTimeout = setTimeout(() => {
        if (!window.__bezonedReactMounted) {
          showCatastrophicError();
        }
      }, REACT_MOUNT_TIMEOUT_MS);
    }
  }

  window.addEventListener("unhandledrejection", (event) => {
    const reason = stringifyForSentry(event.reason);
    const reasonDetails = serializeForSentry(event.reason);

    logFatalError("Unhandled promise rejection", event.reason, {
      reason,
      reasonDetails,
    });
    handleFatalError(event.reason, {
      reason,
      reasonDetails,
    });
  });

  window.addEventListener("error", (event) => {
    const error =
      event.error || new Error(String(event.message || "Unknown window error"));
    const errorDetails = serializeForSentry(error);
    const details = {
      colno: "colno" in event ? event.colno : undefined,
      error: stringifyForSentry(error),
      errorDetails,
      filename: "filename" in event ? event.filename : undefined,
      lineno: "lineno" in event ? event.lineno : undefined,
      message: event.message,
      target: getEventTargetDetails(event.target),
    };
    logFatalError("Window error", error, details);

    handleFatalError(error, details);
  });
})();
